import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import '../pages.css';
import {
  LoadingContext,
  M_StateActions,
  MHeader,
  MButton,
  NavigateContext
} from 'src/M_Components';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from 'src/components/Forms/DynamicForm';

/* =====================================================================================================================================
 * Helpers
 * =====================================================================================================================================*/
const toCamel = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const isBoolean = (opt) => opt?.Type === 'boolean' || opt?.Type === 'bool';

/* =====================================================================================================================================
 * Component
 * =====================================================================================================================================*/
export default function ShipperConfigCrud({ handleTabClose, toastMessages }) {
  const dispatch = useDispatch();
  const M_State = useSelector((state) => state.StateKeyValue);
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);
  const { renameTab } = useContext(NavigateContext);

  // Tab + one-shot globals
  const selectedTab = M_State.find((o) => o.key === 'selectedTab')?.value;
  const globalOperation = M_State.find((obj) => obj.key === 'operation')?.value;
  const globalLine = M_State.find((obj) => obj.key === 'ShipperLine')?.value;

  // Per-tab state
  const [id, setId] = useState(selectedTab);
  const [operation, setOperation] = useState(globalOperation ?? 'Error');
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});
  const [shipperInfo, setShipperInfo] = useState({});
  const [isFirst, setIsFirst] = useState(true);
  const [shipperId, setShipperId] = useState(sessionLine?.Id);

  // Schema & FK cache (para o DynamicForm)
  const [allOptions, setAllOptions] = useState([]); // raw Options para o DF

  // Imperative ref do DynamicForm
  const formRef = useRef(null);

  // Services
  const svcShippers = useMemo(() => new BufferService('Shippers', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcCreate = useMemo(() => new BufferService('Shippers/Create', ctx.DEFAULTURL), [ctx.DEFAULTURL]);
  const svcUpdate = useMemo(() => new BufferService('Shippers/Update', ctx.DEFAULTURL), [ctx.DEFAULTURL]);

  const isViewMode = (operation || '').toLowerCase() === 'view';

  /* =====================================================================================================================================
   * Update tab label (operation/name changes)
   * ===================================================================================================================================*/
  useEffect(() => {
    if (!selectedTab || !operation) return;
    const key =
      operation === 'Create' ? 'LabelNewShipper' :
        operation === 'Edit' ? 'LabelEditShipper' :
          'LabelViewShipper';
    const name = sessionLine?.Name ?? shipperInfo?.Name ?? '';
    const base = t(key);
    const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
  }, [selectedTab, operation, sessionLine?.Name, shipperInfo?.Name]);

  /* =====================================================================================================================================
   * BOOT: restore snapshot -> consume globals -> fetch Options (+ Details if needed)
   * ===================================================================================================================================*/
  useEffect(() => {
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

    const snapInfo = pick('shipperInfo');
    const snapIF = pick('isFirst');
    const snapOP = pick('operation');
    const snapSL = pick('sessionLine');
    const snapOptions = pick('allOptions') ?? pick('formSchema'); // retro compat

    if (snapInfo) setShipperInfo(snapInfo);
    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    const seedOperation = globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) {
      setSessionLine(seedLine);
      if (seedLine.Id) setShipperId(seedLine.Id);
    }
    if (Array.isArray(snapOptions)) setAllOptions(snapOptions);

    // consume one-shot globals
    if (globalOperation !== undefined) dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    if (globalLine !== undefined) dispatch(M_StateActions.removeM_State({ key: 'ShipperLine' }));

    fetchOptions(seedOperation, seedLine, { skipDetails: true }).finally(async () => {
      if ((seedOperation || '').toLowerCase() === 'create') {
        ctx.setIsLoad(false);
      } else if (seedOperation && seedLine?.Id) {
        await fetchDetails(seedLine?.Id);
        ctx.setIsLoad(false);
      } else {
        ctx.setIsLoad(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =====================================================================================================================================
   * SNAPSHOT PERSISTENCE: keep per-tab draft up to date (merge current DF values when not in view)
   * ===================================================================================================================================*/
  useEffect(() => {
    if (!id) return;

    const currentFormValues = formRef.current?.getValues?.() || {};
    const infoToPersist =
      (operation || '').toLowerCase() === 'view'
        ? shipperInfo
        : { ...shipperInfo, ...currentFormValues };

    dispatch(
      M_StateActions.addM_State({
        key: id,
        value: [
          { key: 'shipperInfo', value: infoToPersist },
          { key: 'isFirst', value: isFirst },
          { key: 'operation', value: operation },
          { key: 'sessionLine', value: sessionLine },
          { key: 'allOptions', value: allOptions }
        ]
      })
    );
  }, [id, shipperInfo, isFirst, operation, sessionLine, allOptions, dispatch]);

  /* =====================================================================================================================================
   * TAB CHANGES: restore that tab snapshot or refetch
   * ===================================================================================================================================*/
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);
    const data = M_State.find((obj) => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;
      const tempSI = pick('shipperInfo');
      const tempIF = pick('isFirst');
      const tempOP = pick('operation');
      const tempSL = pick('sessionLine');
      const tempOptions = pick('allOptions') ?? pick('formSchema');

      setShipperInfo(tempSI || {});
      setIsFirst(tempIF ?? false);
      setOperation(tempOP ?? 'Error');
      setSessionLine(tempSL || {});
      if (tempSL?.Id) setShipperId(tempSL.Id);
      if (Array.isArray(tempOptions)) setAllOptions(tempOptions);
      ctx.setIsLoad(false);
      return;
    }
    // Sem snapshot: refetch
    fetchOptions(operation, sessionLine, { skipDetails: true }).finally(async () => {
      if ((operation || '').toLowerCase() !== 'create' && sessionLine?.Id) await fetchDetails(sessionLine?.Id);
      ctx.setIsLoad(false);
    });
  };

  /* =====================================================================================================================================
   * i18n: refresh Options on language change + rebuild label
   * ===================================================================================================================================*/
  useEffect(() => {
    if (!operation || isFirst) return; // skip on first load or no operation
    fetchOptions(operation, sessionLine, { skipDetails: true });

    const key =
      operation === 'Create' ? 'LabelNewShipper' :
        operation === 'Edit' ? 'LabelEditShipper' :
          'LabelViewShipper';
    const name = sessionLine?.Name ?? shipperInfo?.Name ?? '';
    const base = t(key);
    const label = operation === 'Create' || !name ? base : `${base} "${name}"`;
    renameTab(String(selectedTab), label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  /* =====================================================================================================================================
   * fetchOptions: load Options (and all FK datasets) → optionally fetch Details
   * ===================================================================================================================================*/
  const fetchOptions = async (op, line, { skipDetails } = { skipDetails: false }) => {
    ctx.setIsLoad(true);
    try {
      const res = await svcShippers.options();
      if (isFailurePayload(res) || res?.name === 'AxiosError') {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        ctx.setIsLoad(false);
        return;
      }

      setAllOptions(res || []);
      const mode = (op || operation || 'View').toLowerCase();
      if (!skipDetails && mode !== 'create' && (line?.Id || sessionLine?.Id)) {
        await fetchDetails(line?.Id || sessionLine?.Id);
      }
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  };


  /* =====================================================================================================================================
   * Get the entity details by ID
   * ===================================================================================================================================*/
  const fetchDetails = async (idToGet) => {
    ctx.setIsLoad(true);
    try {
      const res = await svcShippers.getId(idToGet);
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      setShipperInfo(res || {});
      setShipperId(res?.Id);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /* =====================================================================================================================================
   * Builds payload from DynamicForm values (Header preferred for FKs; booleans coerced)
   * ===================================================================================================================================*/
  const buildPayloadFromValues = (values) => {
    const options = Array.isArray(allOptions) ? allOptions : [];
    const entries = [];

    for (const opt of options) {
      if (!opt) continue;

      const keySource = opt.IsForeignKey ? (opt.Header || opt.Name) : opt.Name;
      const payloadKey = toCamel(opt.Header || keySource);

      let v;
      if (isBoolean(opt)) {
        v = !!values?.[opt.Name];
      } else if (opt.IsForeignKey) {
        const rawId = values?.[keySource];
        v = rawId == null || rawId === '' ? null : Number(rawId);
      } else {
        v = values?.[keySource];
      }

      if (v !== undefined) entries.push([payloadKey, v]);
    }

    return Object.fromEntries(entries);
  };

  /* =====================================================================================================================================
   * Submit (Create/Update)
   * ===================================================================================================================================*/
  const onSubmitDynamic = useCallback(
    async (values) => {
      ctx.setIsLoad(true);
      try {
        const payload = buildPayloadFromValues(values);
        const isCreate = (operation || '').toLowerCase() === 'create';

        const res = isCreate
          ? await svcCreate.post(payload)
          : await svcUpdate.update(shipperId, payload);

        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }
        toastMessages('success', '', isCreate ? t('ShipperCreated') : t('ShipperUpdated'));
        handleTabClose(id);
      } catch (err) {
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [shipperId, ctx, handleTabClose, id, operation, svcCreate, svcUpdate, t, toastMessages, allOptions]
  );

  /* =====================================================================================================================================
   * Imperative submit
   * ===================================================================================================================================*/
  const triggerFormSubmit = () => {
    formRef.current?.submit?.();
  };

  /* =====================================================================================================================================
   * Refresh (refetch Options + Details if applicable)
   * ===================================================================================================================================*/
  const onRefresh = () => {
    fetchOptions(operation, sessionLine);
  };

  /* =====================================================================================================================================
   * Handles form value changes to update M_State snapshot quickly (draft persistence)
   * ===================================================================================================================================*/
  const handleFormValuesChange = useCallback(
    (patch, meta) => {
      if (!patch || !id) return;

      const prevTabState = M_State.find((o) => o.key === id)?.value || [];
      const pick = (k) => prevTabState.find((x) => x.key === k)?.value;
      const merged = { ...(pick('shipperInfo') || shipperInfo || {}), ...patch };

      const updated = [
        ...prevTabState.filter((x) => x.key !== 'shipperInfo'),
        { key: 'shipperInfo', value: merged }
      ];
      dispatch(M_StateActions.addM_State({ key: id, value: updated }));
    },
    [M_State, id, shipperInfo, dispatch]
  );

  return (
    <>
      <MHeader
        element={
          <>
            <MButton
              tooltip={t('RefreshTable')}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={onRefresh}
              label={t('RefreshTable')}
            />
            {!isViewMode && (
              <MButton
                tooltip={t('Save')}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t('Save')}
              />
            )}
          </>
        }
      />

      <div
        style={{
          padding: '20px',
          background: 'var(--color-white)'
        }}>
        <div className="firstDivContainer">
          <DynamicForm
            key={`df-shipper-${id}-${operation}-${shipperId ?? 'new'}`}
            formRef={formRef}
            mode={(operation || 'View').toLowerCase()}
            schema={allOptions}
            data={shipperInfo}
            onSubmit={onSubmitDynamic}
            onValuesChange={handleFormValuesChange}
            title={t('InfoShippersForm')}
            containerClassName="alignInputsUserForm"
          />
        </div>
      </div>
    </>
  );
}

ShipperConfigCrud.propTypes = {
  handleTabClose: PropTypes.func,
  toastMessages: PropTypes.func
};
